import { storage } from "./storage";
import { chatDebugLog } from "./chatDebug";
import { initMessageActions } from "../actions/initMessageActions";
import { reconcileMessageGrouping } from "../actions/reconcileMessageGrouping";
import { renderAudio } from "../actions/renderAudio";
import { initVideoLoaders } from "../actions/initVideoLoaders";
import { initFancybox } from "../../../../../../themes/growtype/resources/scripts/plugins/fancybox/initFancybox";
import { setupQuiz } from "../actions/questionQuiz";
import { scrollToChatBottom } from "../actions/scrollToChatBottom";

const STORAGE_PREFIX = "growtype_chat_guest_history_v1";
const MAX_HISTORY_ITEMS = 40;

function debugLog(step, details = {}) {
  chatDebugLog('guestHistory', "[guest-history]", step, details);
}

export function shouldUseGuestLocalHistoryMode() {
  const enabled = (
    !!window.growtype_chat_ajax?.guest_local_history_enabled &&
    !window.growtype_chat_ajax?.is_logged_in
  );

  debugLog("shouldUseGuestLocalHistoryMode", {
    guest_local_history_enabled:
      !!window.growtype_chat_ajax?.guest_local_history_enabled,
    is_logged_in: !!window.growtype_chat_ajax?.is_logged_in,
    enabled,
  });

  return enabled;
}

function getRoleplayKey() {
  return (
    window.growtype_chat_ajax?.roleplay_scenario_key || "roleplay_scenario"
  );
}

function buildLegacyHistoryKey(chatForm) {
  const userToken = chatForm.attr("data-user-token") || "guest";
  const path = window.location.pathname || "";
  const roleplay = new URLSearchParams(window.location.search).get(
    getRoleplayKey()
  ) || "";
  const requestContextKey = chatForm.attr("data-request-context-key") || "";

  return [STORAGE_PREFIX, userToken, path, roleplay, requestContextKey].join("::");
}

function getHistoryKeyVariants(chatForm) {
  const userToken = chatForm.attr("data-user-token") || "guest";
  const sessionToken = chatForm.attr("data-session-token") || "";
  const keys = [];

  if (sessionToken) {
    keys.push([STORAGE_PREFIX, userToken, "session", sessionToken].join("::"));
  }

  keys.push(buildLegacyHistoryKey(chatForm));

  return Array.from(new Set(keys.filter((key) => typeof key === "string" && key !== "")));
}

function getHistoryKey(chatForm) {
  const keys = getHistoryKeyVariants(chatForm);
  const key = keys[0] || buildLegacyHistoryKey(chatForm);

  debugLog("getHistoryKey", {
    sessionToken: chatForm.attr("data-session-token") || "",
    variants: keys,
    key,
  });

  return key;
}

function readStoredHistoryRecord(key) {
  const raw = storage.get(key, "local");

  debugLog("readStoredHistoryRecord:start", {
    key,
    hasRaw: !!raw,
    rawLength: raw ? raw.length : 0,
  });

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.messages)) {
      debugLog("readStoredHistoryRecord:invalid-shape", {
        key,
        parsedKeys: parsed && typeof parsed === "object" ? Object.keys(parsed) : [],
      });
      return null;
    }

    const entries = parsed.messages
      .map(normalizeEntry)
      .filter(Boolean)
      .slice(-MAX_HISTORY_ITEMS);

    debugLog("readStoredHistoryRecord:success", {
      key,
      storedCount: parsed.messages.length,
      normalizedCount: entries.length,
    });

    return {
      key,
      entries,
      updatedAt: Number(parsed?.updatedAt || 0),
    };
  } catch (error) {
    console.warn("[guest-history] failed to parse local history", error);
    debugLog("readStoredHistoryRecord:parse-failed", {
      key,
      error: error?.message || String(error),
    });
    return null;
  }
}

function normalizeEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const role = entry.role === "assistant" ? "assistant" : "user";
  const html = typeof entry.html === "string" ? entry.html : "";
  const text = typeof entry.text === "string" ? entry.text : "";
  const payload =
    entry.payload && typeof entry.payload === "object" ? entry.payload : {};
  const category =
    typeof entry.category === "string"
      ? entry.category
      : payload.category || "text";

  if (!html && !text && !hasStructuredPayload(payload)) {
    return null;
  }

  return {
    role,
    html,
    text,
    category,
    payload,
    createdAt: Number(entry.createdAt || Date.now()),
  };
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function normalizeComparableText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeComparableRole(value) {
  const role = normalizeComparableText(value);

  if (role === "assistant" || role === "admin") {
    return "assistant";
  }

  if (role === "user") {
    return "user";
  }

  return "";
}

function looksLikeQuizHtml(value) {
  const html = String(value || "").toLowerCase();

  return (
    html.includes("growtype-quiz-wrapper") ||
    html.includes("growtype-quiz-question") ||
    html.includes("data-chat-inline-answer")
  );
}

function isQuizLikeEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return false;
  }

  if (looksLikeQuizHtml(entry.html || "")) {
    return true;
  }

  const payload = entry.payload && typeof entry.payload === "object"
    ? entry.payload
    : {};

  return (
    (Array.isArray(payload.quiz) && payload.quiz.length > 0) ||
    looksLikeQuizHtml(payload.html || "")
  );
}

function isQuizLikeNode(element) {
  const node = jQuery(element);

  return (
    node.find(".growtype-quiz-wrapper, .growtype-quiz-question, [data-chat-inline-answer=\"true\"]").length > 0
  );
}

function hasStructuredPayload(payload = {}) {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return (
    (Array.isArray(payload.quiz) && payload.quiz.length > 0) ||
    (Array.isArray(payload.images) && payload.images.length > 0) ||
    (Array.isArray(payload.videos) && payload.videos.length > 0) ||
    typeof payload.audio_url === "string" && payload.audio_url.trim() !== ""
  );
}

function buildQuizFallbackHtml(entry) {
  const payload = entry?.payload;
  const quiz = Array.isArray(payload?.quiz) ? payload.quiz : [];
  const firstQuestion = quiz[0];

  if (!firstQuestion || !Array.isArray(firstQuestion.answers) || firstQuestion.answers.length === 0) {
    return "";
  }

  const questionLabel = escapeHtml(firstQuestion.question || "");
  const answersHtml = firstQuestion.answers
    .map((answer) => {
      if (!answer || typeof answer !== "object") {
        return "";
      }

      const answerText = escapeHtml(answer.text || answer.value || "");
      const answerValue = escapeHtml(answer.value || answer.text || "");

      if (!answerText || !answerValue) {
        return "";
      }

      return `
        <button
          type="button"
          class="growtype-quiz-question-answer"
          data-value="${answerValue}"
          data-chat-inline-answer="true"
          data-chat-inline-answer-value="${answerValue}"
        >
          <span class="growtype-quiz-question-answer-content">
            <span class="e-label-main">${answerText}</span>
          </span>
        </button>
      `;
    })
    .filter((item) => item.trim() !== "")
    .join("");

  if (!answersHtml) {
    return "";
  }

  return `
    <div class="growtype-quiz-wrapper">
      <div class="growtype-quiz-question first-question" data-question-style="general">
        ${questionLabel ? `<p class="e-subtitle">${questionLabel}</p>` : ""}
        <div class="growtype-quiz-question-answers-wrapper">
          <div class="growtype-quiz-question-answers">
            ${answersHtml}
          </div>
        </div>
      </div>
    </div>
  `;
}

function buildComparableEntryText(entry) {
  const htmlText = normalizeComparableText(
    typeof entry?.html === "string" && entry.html.trim() !== ""
      ? jQuery("<div>").html(entry.html).text()
      : ""
  );
  const baseText = normalizeComparableText(
    entry?.text ||
    entry?.payload?.main_text ||
    htmlText
  );
  const payload = entry?.payload && typeof entry.payload === "object"
    ? entry.payload
    : {};
  const quiz = Array.isArray(payload.quiz) ? payload.quiz : [];

  const quizText = quiz
    .flatMap((question) => {
      if (!question || typeof question !== "object") {
        return [];
      }

      const answerText = Array.isArray(question.answers)
        ? question.answers
          .map((answer) => answer?.text || answer?.value || "")
          .filter(Boolean)
        : [];

      return [question.question || "", ...answerText];
    })
    .join(" ");

  const combined = [baseText, quizText]
    .map(normalizeComparableText)
    .filter(Boolean)
    .join(" ");

  return combined;
}

function buildComparableEntrySignature(entry) {
  if (!entry || typeof entry !== "object") {
    return "";
  }

  const comparableText = buildComparableEntryText(entry);
  if (!comparableText) {
    return "";
  }

  return JSON.stringify({
    role: isQuizLikeEntry(entry) ? "" : normalizeComparableRole(entry.role),
    category: normalizeComparableText(entry.category || entry?.payload?.category || "text"),
    text: comparableText,
  });
}

function buildStableEntrySequenceToken(entry, index = 0) {
  const signature = buildComparableEntrySignature(entry);

  if (signature) {
    return signature;
  }

  return JSON.stringify({
    fallbackIndex: index,
    role: entry?.role || "",
    text: entry?.text || "",
    category: entry?.category || entry?.payload?.category || "text",
    payload: entry?.payload || {},
  });
}

function findEntrySequenceOverlap(leftEntries = [], rightEntries = []) {
  const maxOverlap = Math.min(leftEntries.length, rightEntries.length);

  if (maxOverlap === 0) {
    return 0;
  }

  const leftTokens = leftEntries.map((entry, index) =>
    buildStableEntrySequenceToken(entry, index)
  );
  const rightTokens = rightEntries.map((entry, index) =>
    buildStableEntrySequenceToken(entry, index)
  );

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    let matches = true;

    for (let index = 0; index < overlap; index += 1) {
      if (
        leftTokens[leftTokens.length - overlap + index] !==
        rightTokens[index]
      ) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return overlap;
    }
  }

  return 0;
}

function mergeHistoryRecordEntries(records = []) {
  const orderedRecords = [...records].sort((left, right) => {
    if (left.updatedAt !== right.updatedAt) {
      return left.updatedAt - right.updatedAt;
    }

    return left.keyIndex - right.keyIndex;
  });

  const mergedEntries = [];

  orderedRecords.forEach((record) => {
    const entries = Array.isArray(record?.entries) ? record.entries : [];

    if (entries.length === 0) {
      return;
    }

    if (mergedEntries.length === 0) {
      mergedEntries.push(...entries);
      return;
    }

    const overlap = findEntrySequenceOverlap(mergedEntries, entries);
    mergedEntries.push(...entries.slice(overlap));
  });

  const normalizedMergedEntries = mergedEntries.slice(-MAX_HISTORY_ITEMS);

  debugLog("mergeHistoryRecordEntries", {
    recordCount: orderedRecords.length,
    mergedCount: normalizedMergedEntries.length,
  });

  return normalizedMergedEntries;
}

function buildComparableNodeSignature(node) {
  const element = jQuery(node);
  if (!element.length || !element.is(".g-chatform-message")) {
    return "";
  }

  const contentInner = element.find(".g-chatform-message-content-inner").first();
  const comparableText = normalizeComparableText(contentInner.text());
  if (!comparableText) {
    return "";
  }

  return JSON.stringify({
    role: isQuizLikeNode(element) ? "" : normalizeComparableRole(element.attr("data-role") || ""),
    category: normalizeComparableText(element.attr("data-message-type") || "text"),
    text: comparableText,
  });
}

function collectRenderedMessageSignatures(nodes) {
  const signatures = new Set();

  nodes.each(function () {
    const signature = buildComparableNodeSignature(this);

    if (signature) {
      signatures.add(signature);
    }
  });

  debugLog("collectRenderedMessageSignatures", {
    count: signatures.size,
  });

  return signatures;
}

function buildFallbackHtml(entry) {
  const escapedText = escapeHtml(entry?.text || "").replace(/\n/g, "<br>");
  const isAssistant = entry?.role === "assistant";
  const quizHtml = isAssistant ? buildQuizFallbackHtml(entry) : "";

  if (!escapedText && !quizHtml) {
    return "";
  }

  if (isAssistant) {
    return `
      <div class="g-chatform-message" data-role="admin" data-message-type="${escapeHtml(entry.category || "text")}">
        <div class="g-chatform-message-inner">
          <div class="g-chatform-message-inner-top">
            <div class="g-chatform-message-content">
              <div class="g-chatform-message-content-inner">
                ${escapedText ? `<p>${escapedText}</p>` : ""}
                ${quizHtml}
              </div>
            </div>
          </div>
          <div class="g-chatform-message-inner-bottom">
            <div class="g-chatform-message-time"></div>
          </div>
        </div>
      </div>
    `;
  }

  return `
    <div class="g-chatform-message" data-role="user" data-message-type="${escapeHtml(entry.category || "text")}">
      <div class="g-chatform-message-inner">
        <div class="g-chatform-message-inner-top">
          <div class="g-chatform-message-content">
            <div class="g-chatform-message-content-inner">
              <p>${escapedText}</p>
            </div>
          </div>
        </div>
        <div class="g-chatform-message-inner-bottom">
          <div class="g-chatform-message-time"></div>
        </div>
      </div>
    </div>
  `;
}

function buildStoredEntryNode(entry) {
  const storedHtml = entry?.role === "user"
    ? ""
    : getRenderableStoredHtml(entry);
  const html = storedHtml || buildFallbackHtml(entry);

  if (typeof html !== "string" || html.trim() === "") {
    return jQuery();
  }

  return jQuery("<div>").html(html).children().first();
}

function collectPreservedNodeBuckets(preservedNodes) {
  const buckets = new Map();

  preservedNodes.each(function () {
    const node = jQuery(this);
    const signature = buildComparableNodeSignature(node);

    if (!signature) {
      return;
    }

    const bucket = buckets.get(signature) || [];
    bucket.push(node);
    buckets.set(signature, bucket);
  });

  debugLog("collectPreservedNodeBuckets", {
    bucketCount: buckets.size,
  });

  return buckets;
}

function takePreservedNodeForEntry(entry, preservedNodeBuckets) {
  const signature = buildComparableEntrySignature(entry);

  if (!signature) {
    return jQuery();
  }

  const bucket = preservedNodeBuckets.get(signature) || [];
  const node = bucket.shift();

  if (!node || !node.length) {
    return jQuery();
  }

  node.attr("data-guest-history-reused", "true");

  if (bucket.length > 0) {
    preservedNodeBuckets.set(signature, bucket);
  } else {
    preservedNodeBuckets.delete(signature);
  }

  return node;
}

function buildRestoredEntryNodes(entries, preservedNodeBuckets) {
  const restoredNodes = [];

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const preservedNode = takePreservedNodeForEntry(entry, preservedNodeBuckets);

    if (preservedNode.length) {
      restoredNodes.push(preservedNode);
      return;
    }

    const storedNode = buildStoredEntryNode(entry);
    if (storedNode.length) {
      restoredNodes.push(storedNode);
    }
  });

  debugLog("buildRestoredEntryNodes", {
    entryCount: Array.isArray(entries) ? entries.length : 0,
    restoredCount: restoredNodes.length,
  });

  return restoredNodes;
}

function collectUnmatchedPreservedNodes(preservedNodes) {
  const unmatchedNodes = [];

  preservedNodes.each(function () {
    const node = jQuery(this);

    if (node.attr("data-guest-history-reused") === "true") {
      node.removeAttr("data-guest-history-reused");
      return;
    }

    unmatchedNodes.push(node);
  });

  debugLog("collectUnmatchedPreservedNodes", {
    unmatchedCount: unmatchedNodes.length,
  });

  return unmatchedNodes;
}

function getRenderableStoredHtml(entry) {
  if (typeof entry?.html !== "string" || entry.html.trim() === "") {
    return "";
  }

  const wrapper = jQuery("<div>").html(entry.html);
  wrapper.find("script, iframe, object, embed, form, style, link, meta").remove();
  wrapper.find("*").each(function () {
    const element = this;

    Array.from(element.attributes || []).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = String(attribute.value || "").trim();

      if (name.startsWith("on")) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (
        ["href", "src", "action", "formaction", "xlink:href"].includes(name) &&
        /^javascript:/i.test(value)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  const message = wrapper.find(".g-chatform-message").first();

  if (!message.length) {
    debugLog("getRenderableStoredHtml:no-message", {
      role: entry?.role || "",
      htmlLength: entry?.html?.length || 0,
    });
    return "";
  }

  const expectedDomRole = entry?.role === "assistant" ? "admin" : "user";
  if (message.attr("data-role") !== expectedDomRole) {
    message.attr("data-role", expectedDomRole);
  }

  const contentInner = message.find(".g-chatform-message-content-inner").first();
  const textContent = contentInner.text().trim();
  const hasRichContent =
    contentInner.find(
      ".growtype-quiz-wrapper, .g-chatform-message-assets-wrapper, .g-chatform-message-img-wrapper, .audio-message, video, img, .gift-wrapper"
    ).length > 0;

  const valid = textContent !== "" || hasRichContent;

  debugLog("getRenderableStoredHtml", {
    role: entry?.role || "",
    valid,
    textLength: textContent.length,
    hasRichContent,
  });

  return valid ? message.prop("outerHTML") || "" : "";
}

export function getGuestLocalHistory(chatForm) {
  const keys = getHistoryKeyVariants(chatForm);

  debugLog("getGuestLocalHistory:start", {
    keys,
  });

  const records = [];

  for (let index = 0; index < keys.length; index++) {
    const key = keys[index];
    const record = readStoredHistoryRecord(key);

    if (!record || record.entries.length === 0) {
      continue;
    }
    records.push({
      ...record,
      keyIndex: index,
    });
  }

  if (records.length === 0) {
    debugLog("getGuestLocalHistory:no-raw", { keys });
    return [];
  }

  records.sort((left, right) => {
    if (right.updatedAt !== left.updatedAt) {
      return right.updatedAt - left.updatedAt;
    }

    return left.keyIndex - right.keyIndex;
  });

  const selectedRecord = records[0];
  const primaryKey = keys[0];
  const mergedEntries = mergeHistoryRecordEntries(records);

  debugLog("getGuestLocalHistory:selected-record", {
    selectedKey: selectedRecord.key,
    selectedUpdatedAt: selectedRecord.updatedAt,
    selectedEntryCount: selectedRecord.entries.length,
    primaryKey,
    candidateCount: records.length,
    mergedEntryCount: mergedEntries.length,
  });

  if (primaryKey && (selectedRecord.key !== primaryKey || records.length > 1)) {
    debugLog("getGuestLocalHistory:migrate-legacy", {
      fromKey: selectedRecord.key,
      toKey: primaryKey,
      entryCount: mergedEntries.length,
      updatedAt: selectedRecord.updatedAt,
    });
    setGuestLocalHistory(chatForm, mergedEntries);
    records.forEach((record) => {
      if (record.key !== primaryKey) {
        storage.remove(record.key, "local");
      }
    });
  }

  return mergedEntries;
}

export function setGuestLocalHistory(chatForm, entries) {
  const key = getHistoryKey(chatForm);
  const normalized = (Array.isArray(entries) ? entries : [])
    .map(normalizeEntry)
    .filter(Boolean)
    .slice(-MAX_HISTORY_ITEMS);

  debugLog("setGuestLocalHistory", {
    key,
    inputCount: Array.isArray(entries) ? entries.length : 0,
    normalizedCount: normalized.length,
    roles: normalized.map((entry) => entry.role),
  });

  storage.set(
    key,
    JSON.stringify({
      version: 1,
      updatedAt: Date.now(),
      messages: normalized,
    }),
    "local"
  );

  const persistedEntries = getGuestLocalHistory(chatForm);
  debugLog("setGuestLocalHistory:verify", {
    key,
    persistedCount: persistedEntries.length,
    persistedRoles: persistedEntries.map((entry) => entry.role),
  });
}

export function appendGuestLocalHistory(chatForm, entries) {
  const existing = getGuestLocalHistory(chatForm);
  debugLog("appendGuestLocalHistory", {
    existingCount: existing.length,
    appendCount: Array.isArray(entries) ? entries.length : 0,
  });
  setGuestLocalHistory(chatForm, existing.concat(entries));
}

export function resolveGuestLocalRoleplayOpeningImage(
  chatForm,
  jobId,
  imageUrl,
  messageHtml = ""
) {
  if (!shouldUseGuestLocalHistoryMode()) {
    return;
  }

  const existing = getGuestLocalHistory(chatForm);
  let updated = false;
  const resolved = existing.map((entry) => {
    const payload = entry?.payload && typeof entry.payload === "object"
      ? entry.payload
      : {};

    if (String(payload.custom_roleplay_opening_image_job_id || "") !== String(jobId || "")) {
      return entry;
    }

    updated = true;
    return {
      ...entry,
      category: "image",
      text: "",
      html: messageHtml || entry.html || "",
      payload: {
        ...payload,
        category: "image",
        images: [{ url: imageUrl }],
        custom_roleplay_opening_image: true,
        custom_roleplay_opening_image_pending: false,
      },
    };
  });

  if (updated) {
    setGuestLocalHistory(chatForm, resolved);
  }
}

export function discardGuestLocalCustomRoleplaySetup(chatForm) {
  const existing = getGuestLocalHistory(chatForm);
  const retained = existing.filter((entry) => {
    const serialized = JSON.stringify(entry).toLowerCase();

    return !serialized.includes("create_custom_roleplay") &&
      !serialized.includes("custom_roleplay_setting") &&
      !serialized.includes("let's create a custom roleplay");
  });

  if (retained.length === existing.length) {
    return;
  }

  debugLog("discardGuestLocalCustomRoleplaySetup", {
    removedCount: existing.length - retained.length,
  });
  setGuestLocalHistory(chatForm, retained);
}

export function clearGuestLocalHistory(chatForm) {
  const keys = getHistoryKeyVariants(chatForm);
  debugLog("clearGuestLocalHistory", { keys });
  keys.forEach((key) => storage.remove(key, "local"));
}

export function migrateGuestLocalHistoryToSessionToken(chatForm, nextSessionToken) {
  if (!chatForm || !chatForm.length) {
    return;
  }

  const targetSessionToken = String(nextSessionToken || "").trim();
  const previousSessionToken = String(chatForm.attr("data-session-token") || "").trim();

  if (!targetSessionToken || !previousSessionToken || targetSessionToken === previousSessionToken) {
    return;
  }

  const previousKeys = getHistoryKeyVariants(chatForm);
  const existingEntries = getGuestLocalHistory(chatForm);

  debugLog("migrateGuestLocalHistoryToSessionToken:start", {
    previousSessionToken,
    targetSessionToken,
    previousKeys,
    existingCount: existingEntries.length,
  });

  if (existingEntries.length === 0) {
    return;
  }

  chatForm.attr("data-session-token", targetSessionToken);
  setGuestLocalHistory(chatForm, existingEntries);
  previousKeys.forEach((key) => storage.remove(key, "local"));

  debugLog("migrateGuestLocalHistoryToSessionToken:complete", {
    targetSessionToken,
    migratedCount: existingEntries.length,
    key: getHistoryKey(chatForm),
  });
}

export function cloneGuestLocalHistoryToSessionToken(chatForm, nextSessionToken) {
  if (!chatForm || !chatForm.length) {
    return;
  }

  const targetSessionToken = String(nextSessionToken || "").trim();
  const sourceSessionToken = String(chatForm.attr("data-session-token") || "").trim();

  if (!targetSessionToken || !sourceSessionToken || targetSessionToken === sourceSessionToken) {
    return;
  }

  const existingEntries = getGuestLocalHistory(chatForm);

  debugLog("cloneGuestLocalHistoryToSessionToken:start", {
    sourceSessionToken,
    targetSessionToken,
    existingCount: existingEntries.length,
  });

  if (existingEntries.length === 0) {
    return;
  }

  const originalSessionToken = sourceSessionToken;

  chatForm.attr("data-session-token", targetSessionToken);
  setGuestLocalHistory(chatForm, existingEntries);
  chatForm.attr("data-session-token", originalSessionToken);

  debugLog("cloneGuestLocalHistoryToSessionToken:complete", {
    sourceSessionToken,
    targetSessionToken,
    clonedCount: existingEntries.length,
  });
}

function initializeRestoredMessages(chatForm) {
  const contentWrapper = chatForm.find(".g-chatform-content");
  removeDuplicateIntroQuizAnswerMessages(contentWrapper);
  normalizeIntroQuizTranscriptOrder(contentWrapper);
  const messages = contentWrapper.find(".g-chatform-message");

  debugLog("initializeRestoredMessages:start", {
    messageCount: messages.length,
  });

  messages.each(function () {
    const message = jQuery(this);
    initMessageActions(message);

    message.find(".growtype-theme-fancybox").each(function (_, element) {
      initFancybox(jQuery(element));
    });

    message.find(".growtype-quiz-wrapper").each(function () {
      const quizWrapper = jQuery(this);

      if (quizWrapper.attr("data-growtype-chat-quiz-initialized") === "true") {
        return;
      }

      setupQuiz(quizWrapper);
      quizWrapper.attr("data-growtype-chat-quiz-initialized", "true");
    });
  });

  contentWrapper.find(".audio-message").each(function () {
    renderAudio(jQuery(this));
  });

  initVideoLoaders(chatForm, "spinner");
  reconcileMessageGrouping(contentWrapper);
  scrollToChatBottom(0, true);

  debugLog("initializeRestoredMessages:complete", {
    messageCount: messages.length,
  });
}

function removeDuplicateIntroQuizAnswerMessages(contentWrapper) {
  if (!contentWrapper || !contentWrapper.length) {
    return;
  }

  const introQuizSlug = new URLSearchParams(window.location.search).get("intro_quiz");
  if (!introQuizSlug) {
    return;
  }

  if (contentWrapper.find(".growtype-quiz-wrapper, .growtype-quiz-question").length === 0) {
    return;
  }

  const adminTexts = new Set(
    contentWrapper
      .find('.g-chatform-message[data-role="admin"] .g-chatform-message-content-inner')
      .toArray()
      .map((node) => normalizeComparableText(jQuery(node).text()))
      .filter(Boolean)
  );

  if (adminTexts.size === 0) {
    return;
  }

  let removedCount = 0;

  contentWrapper.find('.g-chatform-message[data-role="user"]').each(function () {
    const message = jQuery(this);
    const text = normalizeComparableText(
      message.find(".g-chatform-message-content-inner").first().text()
    );

    if (!text || !adminTexts.has(text)) {
      return;
    }

    if (message.find(".growtype-quiz-wrapper, .growtype-quiz-question").length > 0) {
      return;
    }

    message.remove();
    removedCount += 1;
  });

  debugLog("removeDuplicateIntroQuizAnswerMessages", {
    introQuizSlug,
    adminTextCount: adminTexts.size,
    removedCount,
  });
}

function normalizeIntroQuizTranscriptOrder(contentWrapper) {
  if (!contentWrapper || !contentWrapper.length) {
    return;
  }

  const introQuizSlug = new URLSearchParams(window.location.search).get("intro_quiz");
  if (!introQuizSlug) {
    return;
  }

  const messages = contentWrapper.find(".g-chatform-message");
  if (messages.length < 3) {
    return;
  }

  const plainTextEntries = messages
    .toArray()
    .map((node, index) => {
      const message = jQuery(node);
      const contentInner = message.find(".g-chatform-message-content-inner").first();

      return {
        index,
        message,
        role: normalizeComparableRole(message.attr("data-role") || ""),
        text: normalizeComparableText(contentInner.text()),
        hasQuiz: message.find(".growtype-quiz-wrapper, .growtype-quiz-question").length > 0,
      };
    })
    .filter((entry) => entry.text !== "");

  const answerEntry = plainTextEntries.find((entry) => entry.role === "assistant" && !entry.hasQuiz);
  const questionEntry = plainTextEntries.find((entry) => entry.role === "user" && !entry.hasQuiz && entry.index > 0);
  const nextQuestionEntry = plainTextEntries.find((entry) => entry.hasQuiz);

  if (!answerEntry || !questionEntry || !nextQuestionEntry) {
    return;
  }

  if (!(answerEntry.index < questionEntry.index && questionEntry.index < nextQuestionEntry.index)) {
    return;
  }

  questionEntry.message.after(answerEntry.message);

  debugLog("normalizeIntroQuizTranscriptOrder", {
    introQuizSlug,
    answerIndex: answerEntry.index,
    questionIndex: questionEntry.index,
    nextQuestionIndex: nextQuestionEntry.index,
    moved: true,
  });
}

function getAnsweredInlineQuizValues(entries = []) {
  return new Set(
    (Array.isArray(entries) ? entries : [])
      .filter(
        (entry) =>
          entry?.role === "user" &&
          entry?.payload?.frontend_delay_case === "inline_quiz_answer"
      )
      .map((entry) => normalizeComparableText(entry.text || entry.payload?.main_text || ""))
      .filter(Boolean)
  );
}

function getAnsweredInlineQuizSourceIds(entries = []) {
  return new Set(
    (Array.isArray(entries) ? entries : [])
      .filter(
        (entry) =>
          entry?.role === "user" &&
          typeof entry?.payload?.inline_source_message_id === "string"
      )
      .map((entry) => String(entry.payload.inline_source_message_id || "").trim())
      .filter(Boolean)
  );
}

function filterPreservedNodesForAnsweredInlineQuizzes(preservedNodes, entries) {
  const answeredValues = getAnsweredInlineQuizValues(entries);
  const answeredSourceIds = getAnsweredInlineQuizSourceIds(entries);

  if (answeredValues.size === 0 && answeredSourceIds.size === 0) {
    return preservedNodes;
  }

  const filteredNodes = preservedNodes.filter(function () {
    const node = jQuery(this);

    if (!node.is(".g-chatform-message")) {
      return true;
    }

    const messageId = String(node.attr("data-id") || "").trim();
    if (messageId && answeredSourceIds.has(messageId)) {
      return false;
    }

    const quizAnswers = node.find(
      ".growtype-quiz-question-answer[data-value], .growtype-quiz-question-answer[data-chat-inline-answer-value]"
    );

    if (!quizAnswers.length) {
      return true;
    }

    const hasAnsweredValue = quizAnswers
      .toArray()
      .some((answer) => {
        const answerElement = jQuery(answer);
        const answerValue =
          answerElement.attr("data-chat-inline-answer-value") ||
          answerElement.attr("data-value") ||
          answerElement.text();

        return answeredValues.has(normalizeComparableText(answerValue));
      });

    return !hasAnsweredValue;
  });

  debugLog("filterPreservedNodesForAnsweredInlineQuizzes", {
    answeredSourceIds: Array.from(answeredSourceIds),
    answeredValues: Array.from(answeredValues),
    beforeCount: preservedNodes.length,
    afterCount: filteredNodes.length,
  });

  return filteredNodes;
}

export function restoreGuestLocalHistory(chatForm) {
  const shouldUse = shouldUseGuestLocalHistoryMode();
  debugLog("restoreGuestLocalHistory:start", {
    shouldUse,
    hasChatForm: !!chatForm?.length,
  });

  if (!shouldUse) {
    debugLog("restoreGuestLocalHistory:skip-mode-disabled");
    return false;
  }

  const entries = getGuestLocalHistory(chatForm);
  debugLog("restoreGuestLocalHistory:entries-loaded", {
    entryCount: entries.length,
    roles: entries.map((entry) => entry.role),
  });
  if (entries.length === 0) {
    debugLog("restoreGuestLocalHistory:no-entries");
    return false;
  }

  const contentWrapper = chatForm.find(".g-chatform-content");
  if (!contentWrapper.length) {
    debugLog("restoreGuestLocalHistory:no-content-wrapper");
    return false;
  }

  const preservedNodes = filterPreservedNodesForAnsweredInlineQuizzes(
    contentWrapper.children().clone(),
    entries
  );
  const renderedSignatures = collectRenderedMessageSignatures(preservedNodes);
  const preservedNodeBuckets = collectPreservedNodeBuckets(preservedNodes);

  debugLog("restoreGuestLocalHistory:preserved-nodes", {
    preservedCount: preservedNodes.length,
  });

  const missingEntries = entries.filter((entry) => {
    const signature = buildComparableEntrySignature(entry);

    return !signature || !renderedSignatures.has(signature);
  });

  debugLog("restoreGuestLocalHistory:missing-entries", {
    totalCount: entries.length,
    missingCount: missingEntries.length,
  });

  if (missingEntries.length === 0) {
    initializeRestoredMessages(chatForm);
    chatForm.attr("data-guest-history-restored", "true");
    debugLog("restoreGuestLocalHistory:complete-using-server-render", {
      restoredCount: contentWrapper.find(".g-chatform-message").length,
      preservedCount: preservedNodes.length,
    });
    return preservedNodes.length > 0;
  }

  const restoredEntryNodes = buildRestoredEntryNodes(entries, preservedNodeBuckets);
  const unmatchedPreservedNodes = collectUnmatchedPreservedNodes(preservedNodes);

  debugLog("restoreGuestLocalHistory:html-built", {
    entryCount: entries.length,
    restoredEntryCount: restoredEntryNodes.length,
    unmatchedPreservedCount: unmatchedPreservedNodes.length,
  });

  if (restoredEntryNodes.length === 0) {
    contentWrapper.empty().append(preservedNodes);
    initializeRestoredMessages(chatForm);
    chatForm.attr("data-guest-history-restored", "true");
    debugLog("restoreGuestLocalHistory:complete-no-missing-html", {
      restoredCount: contentWrapper.find(".g-chatform-message").length,
      preservedCount: preservedNodes.length,
    });
    return preservedNodes.length > 0;
  }

  contentWrapper.empty();
  unmatchedPreservedNodes.forEach((node) => {
    contentWrapper.append(node);
  });
  restoredEntryNodes.forEach((node) => {
    contentWrapper.append(node);
  });
  initializeRestoredMessages(chatForm);
  chatForm.attr("data-guest-history-restored", "true");
  debugLog("restoreGuestLocalHistory:complete", {
    restoredCount: contentWrapper.find(".g-chatform-message").length,
    preservedCount: preservedNodes.length,
  });
  return true;
}

export function getGuestLocalHistoryForRequest(chatForm) {
  const entries = getGuestLocalHistory(chatForm).map((entry) => ({
    role: entry.role,
    text: entry.text,
    category: entry.category,
    payload: entry.payload,
  }));

  debugLog("getGuestLocalHistoryForRequest", {
    count: entries.length,
    roles: entries.map((entry) => entry.role),
  });

  return entries;
}

export function buildGuestLocalUserEntry(message, html = "") {
  if (!message || typeof message !== "object") {
    debugLog("buildGuestLocalUserEntry:invalid-message");
    return null;
  }

  const rawValue =
    typeof message.value === "string"
      ? message.value
      : Array.isArray(message.quiz_answers)
        ? ""
        : "";

  let text = rawValue;
  if (
    message.quiz_answers &&
    Object.values(message.quiz_answers)?.[0]?.[0] &&
    typeof Object.values(message.quiz_answers)[0][0] === "string"
  ) {
    text = Object.values(message.quiz_answers)[0][0];
  }

  const entry = normalizeEntry({
    role: "user",
    text,
    html: "",
    category: message.category || "text",
    payload: {
      category: message.category || "text",
      main_text: text,
      frontend_delay_case:
        typeof message.frontend_delay_case === "string"
          ? message.frontend_delay_case
          : "default",
      inline_source_message_id:
        typeof message.inline_source_message_id === "string"
          ? message.inline_source_message_id
          : typeof message.message_id === "string"
            ? message.message_id
            : "",
    },
    createdAt: Date.now(),
  });

  debugLog("buildGuestLocalUserEntry", {
    category: message.category || "text",
    textLength: text.length,
    frontendDelayCase:
      typeof message.frontend_delay_case === "string"
        ? message.frontend_delay_case
        : "default",
    inlineSourceMessageId:
      typeof message.inline_source_message_id === "string"
        ? message.inline_source_message_id
        : typeof message.message_id === "string"
          ? message.message_id
          : "",
    built: !!entry,
  });

  return entry;
}

export function buildGuestLocalRenderedMessageEntry(messageElement) {
  const message = jQuery(messageElement);

  if (!message.length || !message.is(".g-chatform-message")) {
    debugLog("buildGuestLocalRenderedMessageEntry:invalid-message");
    return null;
  }

  const html = message.prop("outerHTML") || "";
  const text = normalizeComparableText(
    message.find(".g-chatform-message-content-inner").first().text()
  );
  const roleAttr = normalizeComparableRole(message.attr("data-role") || "");
  const role = roleAttr === "assistant" ? "assistant" : "user";
  const category = String(message.attr("data-message-type") || "text").trim() || "text";

  const entry = normalizeEntry({
    role,
    html,
    text,
    category,
    payload: {
      category,
      main_text: text,
      frontend_delay_case: "inline_quiz_source_message",
    },
    createdAt: Date.now(),
  });

  debugLog("buildGuestLocalRenderedMessageEntry", {
    role,
    category,
    textLength: text.length,
    built: !!entry,
  });

  return entry;
}

export function maybeImportGuestLocalHistory(chatForm) {
  debugLog("maybeImportGuestLocalHistory:start", {
    guest_local_history_enabled:
      !!window.growtype_chat_ajax?.guest_local_history_enabled,
    is_logged_in: !!window.growtype_chat_ajax?.is_logged_in,
  });

  if (
    !window.growtype_chat_ajax?.guest_local_history_enabled ||
    !window.growtype_chat_ajax?.is_logged_in
  ) {
    debugLog("maybeImportGuestLocalHistory:skip");
    return Promise.resolve(false);
  }

  const entries = getGuestLocalHistory(chatForm);
  if (entries.length === 0) {
    debugLog("maybeImportGuestLocalHistory:no-entries");
    return Promise.resolve(false);
  }

  const roleplayKey = getRoleplayKey();

  return new Promise((resolve) => {
    jQuery.ajax({
      url: growtype_chat_ajax.url,
      type: "post",
      data: {
        action: growtype_chat_ajax.guest_history_import_action,
        nonce: growtype_chat_ajax.nonce,
        user: {
          token: chatForm.attr("data-user-token"),
        },
        session: {
          token: chatForm.attr("data-session-token"),
          url: window.location.href,
          bot_profile:
            window.growtype_chat?.[chatForm.attr("id")]?.bot_profile || "",
          local_history: getGuestLocalHistoryForRequest(chatForm),
          [roleplayKey]:
            new URLSearchParams(window.location.search).get(roleplayKey) || "",
        },
      },
      success: function (response) {
        debugLog("maybeImportGuestLocalHistory:success", {
          success: !!response?.success,
          imported: response?.data?.imported,
          session_token: response?.data?.session_token || "",
        });

        if (response?.success) {
          clearGuestLocalHistory(chatForm);

          if (response.data?.session_token) {
            chatForm.attr("data-session-token", response.data.session_token);
          }

          window.location.reload();
          resolve(true);
          return;
        }

        resolve(false);
      },
      error: function () {
        debugLog("maybeImportGuestLocalHistory:error");
        resolve(false);
      },
    });
  });
}
